import type Phaser from "phaser";
import { ResolutionGovernor, type ResolutionMode } from "./resolution";

/** Rendering above 2× costs a lot of fill-rate for little visible gain. */
export const MAX_PIXEL_RATIO = 2;

let highDpi = true;
let reapply: (() => void) | null = null;

export function pixelRatio(): number {
	if (!highDpi) return 1;
	return Math.min(Math.max(window.devicePixelRatio || 1, 1), MAX_PIXEL_RATIO);
}

/** Enables/disables full-density rendering and re-applies the canvas size at once. */
export function setHighDpi(enabled: boolean): void {
	highDpi = enabled;
	reapply?.();
}

/** Canvas size in device pixels for a container measured in CSS pixels. */
export function canvasSize(cssWidth: number, cssHeight: number, ratio: number) {
	return {
		width: Math.max(1, Math.round(cssWidth * ratio)),
		height: Math.max(1, Math.round(cssHeight * ratio)),
	};
}

/**
 * Keeps the canvas sharp on high-DPI screens. Phaser's RESIZE mode maps canvas pixels 1:1
 * to CSS pixels (blurry on retina), so the game runs in scale mode NONE at
 * CSS size × devicePixelRatio and is displayed at zoom 1/ratio. Re-applied on window
 * resize and when the page moves to a screen with a different pixel ratio.
 */
export function keepCanvasSharp(game: Phaser.Game, container: HTMLElement): () => void {
	let media: MediaQueryList | undefined;

	const apply = () => {
		const ratio = pixelRatio();
		const cssW = container.clientWidth;
		const cssH = container.clientHeight;
		const { width, height } = canvasSize(cssW, cssH, ratio);
		game.scale.setZoom(1 / ratio);
		game.scale.resize(width, height);
		game.canvas.style.width = `${cssW}px`;
		game.canvas.style.height = `${cssH}px`;
		watchRatio(ratio);
	};

	const watchRatio = (ratio: number) => {
		media?.removeEventListener("change", apply);
		media = window.matchMedia(`(resolution: ${window.devicePixelRatio || ratio}dppx)`);
		media.addEventListener("change", apply);
	};

	const observer = new ResizeObserver(apply);
	observer.observe(container);
	reapply = apply;
	apply();

	return () => {
		reapply = null;
		observer.disconnect();
		media?.removeEventListener("change", apply);
	};
}

const governor = new ResolutionGovernor();
let resolutionMode: ResolutionMode = "auto";

/** Applies the resolution setting (P10.13); choosing Auto again gives sharp rendering another try. */
export function setResolutionMode(mode: ResolutionMode): void {
	resolutionMode = mode;
	governor.reset();
	delete document.body.dataset.resolution;
	setHighDpi(mode !== "standard");
}

/**
 * Auto mode: watches frame times and drops to 1× pixel density once rendering stays slow
 * (see resolution.ts). Marks it on body[data-resolution="reduced"].
 */
export function watchFrameRate(game: Phaser.Game): void {
	game.events.on("step", (_time: number, delta: number) => {
		if (!governor.frame(delta) || resolutionMode !== "auto" || pixelRatio() <= 1) return;
		setHighDpi(false);
		document.body.dataset.resolution = "reduced";
		console.info("gravity: rendering at 1× pixel density (frames were slow at high density)");
	});
}

/** Toggles browser fullscreen for the whole app (canvas + DOM UI overlay). */
export function toggleFullscreen(game: Phaser.Game): void {
	game.scale.toggleFullscreen();
}
